// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DetailedAsset } from '@/asset/api/types';
import {
  getRemainingLifeMonths,
  getTooltipInitialCostBasis,
  getTooltipPeriodSubLabel,
} from '@/asset/components/depreciation-chart-utilities';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import {
  formatAmountWithCurrency,
  formatCurrencyValue,
} from '@/currency/utilities/format';
import { pluralize } from '@/utilities/pluralize';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { DateTime } from 'luxon';

interface Props {
  acquisitionCost: number | null;
  capitalizedCost: number | null;
  endOfUsefulLife: DateTime | null;
  exitDate: DateTime | null;
  inServiceDate: DateTime | null;
  currencyCode: IsoCurrencyCode;
  periods: NonNullable<DetailedAsset['accounting']['depreciationPeriods']>;
  salvageValue: number | null;
  usefulLifeMonths: number | null;
}

export function DepreciationScheduleTable(props: Props) {
  const formatMoney = (value: number | null) =>
    formatAmountWithCurrency(
      value === null ? null : new CurrencyDecimal(value),
      props.currencyCode,
      { sparse: true }
    );

  return (
    <TableContainer
      component={Paper}
      data-testid="depreciation-schedule-table"
      sx={{
        maxWidth: '100%',
        overflowX: 'auto',
      }}
      variant="outlined"
    >
      <Table size="small" sx={{ minWidth: 920 }}>
        <TableHead>
          <TableRow>
            <TableCell>Month</TableCell>
            <TableCell>Period</TableCell>
            <TableCell>Remaining life</TableCell>
            <TableCell align="right">Cost basis</TableCell>
            <TableCell align="right">Salvage value</TableCell>
            <TableCell align="right">Depreciation in month</TableCell>
            <TableCell align="right">Accumulated depreciation</TableCell>
            <TableCell align="right">Net book value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.periods.map((period) => {
            const remainingLifeMonths = getRemainingLifeMonths({
              date: period.date,
              endOfUsefulLife: props.endOfUsefulLife,
              exitDate: props.exitDate,
              inServiceDate: props.inServiceDate,
            });

            return (
              <TableRow key={period.date.toISODate()}>
                <TableCell>{period.date.toFormat('LLLL yyyy')}</TableCell>
                <TableCell>
                  {getTooltipPeriodSubLabel({
                    date: period.date,
                    inServiceDate: props.inServiceDate,
                    usefulLifeMonths: props.usefulLifeMonths,
                  })}
                </TableCell>
                <TableCell>
                  {remainingLifeMonths === null
                    ? '—'
                    : pluralize(remainingLifeMonths, { singular: 'month' })}
                </TableCell>
                <TableCell align="right">
                  {formatMoney(
                    getTooltipInitialCostBasis({
                      acquisitionCost: props.acquisitionCost,
                      capitalizedCost: props.capitalizedCost,
                      inServiceDate: props.inServiceDate,
                      tooltipDate: period.date,
                    })
                  )}
                </TableCell>
                <TableCell align="right">
                  {props.salvageValue === null
                    ? '—'
                    : formatMoney(props.salvageValue)}
                </TableCell>
                <TableCell align="right">
                  {formatCurrencyValue(period.depreciation, { sparse: true })}
                </TableCell>
                <TableCell align="right">
                  {formatCurrencyValue(period.accumulated, { sparse: true })}
                </TableCell>
                <TableCell align="right">
                  {formatCurrencyValue(period.bookValue, { sparse: true })}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
